import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatError } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import VerifyBarcodeDialog from "@/components/VerifyBarcodeDialog";
import { FileDown, Plus, Package, List, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminReamsPage() {
  const cfg = useConfig();
  const [reams, setReams] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", sheets: 2 });
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailCodes, setDetailCodes] = useState([]);

  const load = async () => {
    try {
      const [r, u] = await Promise.all([api.get("/reams"), api.get("/admin/users")]);
      setReams(r.data);
      setUsers(u.data.filter((x) => x.role === "citizen" || x.role === "business"));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.user_id) return toast.error("Seleziona un utente");
    const perSheet = (cfg.ream_layout?.columns || 2) * (cfg.ream_layout?.rows || 4);
    const total = parseInt(form.sheets) * perSheet;
    if (total < 10) return toast.error("La risma deve contenere almeno 10 barcode — aumenta il numero di fogli");
    setBusy(true);
    try {
      await api.post("/reams", { user_id: form.user_id, sheets: parseInt(form.sheets) });
      toast.success("Risma generata");
      setOpen(false);
      setForm({ user_id: "", sheets: 2 });
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const openDetail = async (ream) => {
    setDetail(ream);
    try {
      const { data } = await api.get(`/reams/${ream.id}/barcodes`);
      setDetailCodes(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  const download = async (ream) => {
    try {
      const r = await api.get(`/reams/${ream.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `risma_${ream.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Layout
      title="Risme di codici a barre"
      subtitle="Genera risme di etichette barcode e associale a cittadini o aziende. Ogni risma è un blocco di seriali univoci con codice di controllo."
      actions={
        <>
          <VerifyBarcodeDialog trigger={<Button variant="outline" className="rounded-full gap-2" data-testid="btn-verify-open"><ShieldCheck className="w-4 h-4" /> Verifica barcode</Button>} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="btn-new-ream"><Plus className="w-4 h-4" /> Nuova risma</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Genera risma barcode</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Assegna a</Label>
                  <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="ream-user-select"><SelectValue placeholder="Seleziona utente…" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`ream-user-opt-${u.id}`}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Numero di fogli A4</Label>
                  <Input type="number" min="1" max="50" value={form.sheets} onChange={(e) => setForm({ ...form, sheets: e.target.value })} data-testid="ream-sheets" />
                </div>
                <div className="text-sm text-muted-foreground p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  Layout attuale: <strong>{cfg.ream_layout?.columns || 2} × {cfg.ream_layout?.rows || 4}</strong> = {(cfg.ream_layout?.columns || 2) * (cfg.ream_layout?.rows || 4)} etichette per foglio.<br />
                  Totale generato: <strong>{(parseInt(form.sheets) || 0) * ((cfg.ream_layout?.columns || 2) * (cfg.ream_layout?.rows || 4))}</strong> barcode univoci.
                  <div className="text-xs mt-1">Modifica il layout in <a href="/admin/settings" className="underline">Impostazioni</a>.</div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={create} disabled={busy} data-testid="ream-submit">Genera</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <Card>
        <CardContent className="p-0">
          {reams.length === 0 ? (
            <div className="p-10 text-center">
              <Package className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <div className="font-display text-lg font-semibold">Nessuna risma generata</div>
              <p className="text-sm text-muted-foreground mt-1">Crea la prima risma per iniziare a distribuire etichette barcode.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Assegnatario</TableHead><TableHead>Serie</TableHead><TableHead>Barcode</TableHead><TableHead>Creata il</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {reams.map((r) => (
                  <TableRow key={r.id} className="hover:bg-gray-50" data-testid={`ream-row-${r.id}`}>
                    <TableCell><div className="font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.user_email}</div></TableCell>
                    <TableCell className="font-mono text-xs">{String(r.serial_start).padStart(6, "0")} — {String(r.serial_end).padStart(6, "0")}</TableCell>
                    <TableCell>{r.sheets} fogli · {r.barcodes_per_sheet}/foglio = <strong>{r.serial_end - r.serial_start + 1}</strong></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("it-IT")}</TableCell>
                    <TableCell className="text-right pr-6 space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(r)} className="gap-1" data-testid={`ream-list-${r.id}`}>
                        <List className="w-4 h-4" /> Codici
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => download(r)} className="rounded-full gap-1" data-testid={`ream-dl-${r.id}`}>
                        <FileDown className="w-4 h-4" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Codici della risma</DialogTitle></DialogHeader>
          {detail && (
            <>
              <div className="text-sm text-muted-foreground mb-3">
                Assegnata a <strong>{detail.user_name}</strong> ({detail.user_email}) · seriali {String(detail.serial_start).padStart(6, "0")}–{String(detail.serial_end).padStart(6, "0")}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {detailCodes.map((b) => (
                  <div key={b.serial} data-testid={`code-${b.full_code}`} className={`p-3 rounded-lg border font-mono text-sm text-center ${b.is_used ? "bg-gray-100 border-gray-200 text-muted-foreground line-through" : "bg-white border-primary/20"}`}>
                    {b.full_code}
                    <div className="text-[10px] uppercase tracking-widest mt-1 text-muted-foreground">
                      {b.is_used ? "usato" : "disponibile"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
